import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import StatusBadge from '../components/StatusBadge';
import { useApi } from '../lib/api';
import { formatBytes, formatCents, formatRelative } from '../lib/format';

type Row = {
  id: string;
  slug: string;
  recipient_name: string;
  occasion_type: string | null;
  status: string;
  created_at: string;
  published_at: string | null;
  organizer_email: string | null;
  payment_status: string | null;
  amount_cents: number | null;
  currency: string | null;
  guest_count: number;
  upload_count: number;
  db_bytes: string;
};

type Resp = { rows: Row[]; total: number; limit: number; offset: number };

export default function Experiences() {
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [q, setQ] = useState<string>('');
  const [debouncedQ, setDebouncedQ] = useState<string>('');

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  const path = useMemo(() => {
    const sp = new URLSearchParams();
    if (statusFilter) sp.set('status', statusFilter);
    if (debouncedQ) sp.set('q', debouncedQ);
    sp.set('limit', '100');
    return `/api/experiences?${sp}`;
  }, [statusFilter, debouncedQ]);

  const { data, error, loading } = useApi<Resp>(path);

  return (
    <div>
      <PageHeader
        title="Experiences"
        subtitle={data ? `${data.rows.length} of ${data.total} total` : undefined}
      />

      <div className="mb-4 flex flex-wrap gap-2">
        <input
          type="search"
          placeholder="Search by organizer email or recipient name…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="flex-1 min-w-[260px] px-3 py-2 rounded-md border border-slate-300 bg-white text-sm"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 rounded-md border border-slate-300 bg-white text-sm"
        >
          <option value="">All statuses</option>
          <option value="draft">Draft</option>
          <option value="published">Published</option>
          <option value="archived">Archived</option>
        </select>
      </div>

      {loading && <p className="text-sm text-slate-500">Loading…</p>}
      {error && <p className="text-sm text-red-600">Error: {error.message}</p>}

      {data && (
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2 font-medium">Recipient</th>
                <th className="px-4 py-2 font-medium">Organizer</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 font-medium">Payment</th>
                <th className="px-4 py-2 font-medium text-right">Guests</th>
                <th className="px-4 py-2 font-medium text-right">Uploads</th>
                <th className="px-4 py-2 font-medium text-right">Size (DB)</th>
                <th className="px-4 py-2 font-medium">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.rows.map((row) => (
                <tr key={row.id} className="hover:bg-slate-50">
                  <td className="px-4 py-2">
                    <Link to={`/experiences/${row.id}`} className="text-blue-700 hover:underline">
                      {row.recipient_name}
                    </Link>
                    {row.occasion_type && (
                      <span className="ml-2 text-xs text-slate-500">{row.occasion_type}</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-slate-700">{row.organizer_email ?? '—'}</td>
                  <td className="px-4 py-2"><StatusBadge status={row.status} /></td>
                  <td className="px-4 py-2">
                    {row.payment_status ? (
                      <span className="flex items-center gap-2">
                        <StatusBadge status={row.payment_status} />
                        {row.amount_cents != null && (
                          <span className="text-xs text-slate-600">
                            {formatCents(row.amount_cents, row.currency ?? 'EUR')}
                          </span>
                        )}
                      </span>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">{row.guest_count}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{row.upload_count}</td>
                  <td className="px-4 py-2 text-right tabular-nums" title="Excludes avatars (only uploads.file_size_bytes)">
                    {formatBytes(Number(row.db_bytes))}
                  </td>
                  <td className="px-4 py-2 text-slate-600" title={row.created_at}>
                    {formatRelative(row.created_at)}
                  </td>
                </tr>
              ))}
              {data.rows.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-slate-500">
                    No experiences match these filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
